import React from 'react';
import './RatingStars.css';

/**
 * RatingStars Component
 * Displays star ratings (read-only or interactive)
 *
 * Props:
 * - rating: number (1-5)
 * - maxRating: number (default 5)
 * - size: string ('small', 'medium', 'large')
 * - interactive: boolean (allow clicking to set rating)
 * - onRatingChange: function (callback when rating changes)
 */
const RatingStars = ({
  rating = 0,
  maxRating = 5,
  size = 'medium',
  interactive = false,
  onRatingChange = null
}) => {
  const [hoverRating, setHoverRating] = React.useState(0);

  const handleClick = (value) => {
    if (interactive && onRatingChange) {
      onRatingChange(value);
    }
  };

  const handleMouseEnter = (value) => {
    if (interactive) {
      setHoverRating(value);
    }
  };

  const handleMouseLeave = () => {
    if (interactive) {
      setHoverRating(0);
    }
  };

  const getStarClass = (starValue) => {
    const displayRating = interactive && hoverRating > 0 ? hoverRating : rating;

    if (displayRating >= starValue) {
      return 'star-filled';
    } else if (displayRating >= starValue - 0.5) {
      return 'star-half';
    }
    return 'star-empty';
  };

  return (
    <div className={`rating-stars rating-${size} ${interactive ? 'rating-interactive' : ''}`}>
      {[...Array(maxRating)].map((_, index) => {
        const starValue = index + 1;
        return (
          <span
            key={starValue}
            className={`star ${getStarClass(starValue)}`}
            onClick={() => handleClick(starValue)}
            onMouseEnter={() => handleMouseEnter(starValue)}
            onMouseLeave={handleMouseLeave}
            role={interactive ? 'button' : 'img'}
            aria-label={`${starValue} star${starValue > 1 ? 's' : ''}`}
            tabIndex={interactive ? 0 : -1}
          >
            ★
          </span>
        );
      })}
      {rating > 0 && (
        <span className="rating-value" aria-label={`Rating: ${rating} out of ${maxRating}`}>
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

export default RatingStars;
